import {
  IS_UK_PRACTICE_SEARCH_ENABLED,
  IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED,
} from '@/components/constants/flags';
import { getPhoneNumberVariantForCountry } from '@/utils/country-config';
import type { PersonalizationStep } from '@heidi/growth/contracts';

import type { PersonalizationFormData } from './personalizationTypes';

// Step order, variant membership, and back behaviour live in
// `personalization.wizard.ts` (run by `usePersonalizationForm`). This module
// keeps the per-step form validators — they gate the Continue button
// (`isCurrentStepValid`), deliberately NOT wizard `validate` guards:
// programmatic advances (skip handlers, auto-transitions) must never be
// refused by form validity.

function isNameValid(fd: PersonalizationFormData): boolean {
  return fd.firstName.trim() !== '' && fd.lastName.trim() !== '';
}

function isRoleValid(fd: PersonalizationFormData): boolean {
  const baseValid =
    fd.specialty !== null &&
    fd.role !== null &&
    fd.country !== null &&
    fd.displayLanguage !== null;

  if (!baseValid) return false;

  const phoneVariant = getPhoneNumberVariantForCountry(fd.country?.countryCode);
  if (phoneVariant === 'required' && fd.phoneNumber.trim() === '') {
    return false;
  }

  return true;
}

function isAboutYouValid(fd: PersonalizationFormData): boolean {
  // `role` is required here for the same reason it is in `isRoleValid`: the step
  // renders the dropdown, and `useAboutYouSteps` reveals it no later than
  // `country` under either reveal order — so requiring it can't strand a user on
  // a field they can't see, and leaving it out let them submit the step with the
  // role they were just asked for still unset.
  const baseValid =
    fd.firstName.trim() !== '' &&
    fd.lastName.trim() !== '' &&
    fd.specialty !== null &&
    fd.role !== null &&
    fd.country !== null;

  if (!baseValid) return false;

  const phoneVariant = getPhoneNumberVariantForCountry(fd.country?.countryCode);
  if (phoneVariant === 'required' && fd.phoneNumber.trim() === '') {
    return false;
  }

  return true;
}

function isClinicValid(fd: PersonalizationFormData): boolean {
  const requiresOfficialPracticeSelection =
    fd.country?.countryCode === 'GB' &&
    IS_UK_PRACTICE_SEARCH_ENABLED() &&
    IS_UK_PRACTICE_SEARCH_MANDATORY_ENABLED();

  if (requiresOfficialPracticeSelection) {
    return fd.clinicName.trim() !== '' && fd.isOfficialPractice === true;
  }

  // Only clinic name is required — "How many clinicians do you work with?"
  // (clinicSize) is optional and matches the EMR/EHR pattern below it.
  return fd.clinicName.trim() !== '';
}

function isTemplateCreationValid(fd: PersonalizationFormData): boolean {
  return fd.templateDescription.trim() !== '';
}

function isTemplateReviewValid(fd: PersonalizationFormData): boolean {
  return fd.generatedTemplateStructure !== null;
}

const alwaysValid = (): boolean => true;

export const STEP_VALIDATORS: Record<
  PersonalizationStep,
  (formData: PersonalizationFormData) => boolean
> = {
  'about-you': isAboutYouValid,
  name: isNameValid,
  role: isRoleValid,
  clinic: isClinicValid,
  'invite-team': alwaysValid,
  'tour-invite-team': alwaysValid,
  'mic-permissions': alwaysValid,
  'mic-test': alwaysValid,
  'note-ready': alwaysValid,
  'note-edit': alwaysValid,
  // The tour steps are exploratory: every task is optional, exactly as the
  // accordion tasks they replace, so Continue is never form-gated.
  'tour-detail': alwaysValid,
  'tour-artefacts': alwaysValid,
  'tour-evidence': alwaysValid,
  'template-creation': isTemplateCreationValid,
  'template-loading': alwaysValid,
  'template-review': isTemplateReviewValid,
  'document-selection': alwaysValid,
  completion: alwaysValid,
};

export function validateStep(
  step: PersonalizationStep,
  formData: PersonalizationFormData
): boolean {
  return STEP_VALIDATORS[step](formData);
}

export function isStepValidForCurrentStep(
  currentStep: PersonalizationStep,
  formData: PersonalizationFormData
): boolean {
  return validateStep(currentStep, formData);
}
